import { createContext, useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { loadClientConnections, loadClientProjects } from '../../services/clientPortalService';
import { moduleText as ui } from '../../i18n/moduleText';
import './ClientPortal.css';

const Context = createContext(null);
export const useClientPortal = () => useContext(Context);

export function useClientConnections() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ uid: null, loading: true, connections: [], error: '' });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setResult({ uid, loading: true, connections: [], error: '' });
    if (!uid) {
      setResult({ uid, loading: false, connections: [], error: '' });
      return () => controller.abort();
    }
    loadClientConnections(controller.signal).then(data => {
      if (active) setResult({ uid, loading: false, connections: data.connections || [], error: '' });
    }).catch(error => {
      if (active) setResult({ uid, loading: false, connections: [], error: error.message });
    });
    return () => { active = false; controller.abort(); };
  }, [uid, attempt]);
  return { ...(result.uid === uid ? result : { loading: true, connections: [], error: '' }), retry: () => setAttempt(value => value + 1) };
}

export function ClientPortalProvider({ children }) {
  useTranslation();
  const { connections, loading, error, retry } = useClientConnections();
  const { userData } = useAuth();
  const [params] = useSearchParams();
  const selected = params.get('connection');
  // An invalid explicit connection must never open another company's portal.
  const connection = selected ? connections.find(row => row.id === selected) : connections[0];
  const portalPath = path => `${path}?connection=${encodeURIComponent(connection.id)}`;
  if (loading || error || !connection) return (
    <main className="client-portal-status">
      <h1>{ui('My Consulting Projects')}</h1>
      <LanguageSwitcher compact />
      <p role={error ? 'alert' : 'status'}>{ui(loading ? 'Loading your client connections...' : error || (selected ? 'This client connection is not available.' : 'No client connection is available yet. Ask your service provider to connect your SBS login.'))}</p>
      {!loading && <button type="button" onClick={retry}>{ui('Try again')}</button>}
      {userData?.role !== 'client' && <Link to="/dashboard">{ui('Back to My Dashboard')}</Link>}
    </main>
  );
  return <Context.Provider value={{ connections, connection, portalPath }}>{children}</Context.Provider>;
}

export function usePortalProjects(projectId) {
  const { connection } = useClientPortal();
  const { currentUser } = useAuth();
  const key = `${currentUser?.uid}/${connection.id}/${projectId || ''}`;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ key: null, loading: true, projects: [], project: null, error: '' });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setResult({ key, loading: true, projects: [], project: null, error: '' });
    loadClientProjects(connection.id, projectId, controller.signal).then(data => {
      if (active) setResult({ key, loading: false, projects: data.projects || [], project: data.project || null, error: '' });
    }).catch(error => {
      if (active) setResult({ key, loading: false, projects: [], project: null, error: error.message });
    });
    return () => { active = false; controller.abort(); };
  }, [key, connection.id, projectId, attempt]);
  return { ...(result.key === key ? result : { loading: true, projects: [], project: null, error: '' }), retry: () => setAttempt(value => value + 1) };
}

export function PortalLoadError({ error, retry }) {
  return <div className="client-empty-state" role="alert"><p>{ui(error)}</p><button type="button" onClick={retry}>{ui('Try again')}</button></div>;
}
